import {DateTime} from "luxon";
import {
    EMOTORAD__EVENT__TESTRIDES__TEST_RIDE_DATE__COLUMN_ID,
    EMOTORAD__EVENT__TESTRIDES__TEST_RIDE_TIME__COLUMN_ID,
} from "~/domainModels/workspaceSpecific/emotorad";
import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";

export type EmotoradTestRideTimeOption = {
    value: string;
    label: string;
};

const EMOTORAD_TEST_RIDE_SCHEDULE_ZONE = "Asia/Kolkata";

function parseTestRideDateColumnInstantForSplit(dateRaw: unknown): DateTime | null {
    if (dateRaw instanceof Date) {
        if (Number.isNaN(dateRaw.getTime()) === true) {
            return null;
        }
        return DateTime.fromJSDate(dateRaw, {zone: EMOTORAD_TEST_RIDE_SCHEDULE_ZONE});
    }
    if (typeof dateRaw === "string" || typeof dateRaw === "number") {
        const trimmed = String(dateRaw).trim();
        if (trimmed === "") {
            return null;
        }
        let dt = DateTime.fromISO(trimmed, {setZone: true});
        if (dt.isValid === true) {
            return dt.setZone(EMOTORAD_TEST_RIDE_SCHEDULE_ZONE);
        }
        const dmyFormats = [
            "dd/MM/yyyy HH:mm",
            "d/M/yyyy H:mm",
            "d/M/yyyy HH:mm",
            "dd/MM/yyyy H:mm",
            "dd/MM/yyyy",
            "d/M/yyyy",
        ] as const;
        for (const fmt of dmyFormats) {
            const parsed = DateTime.fromFormat(trimmed, fmt, {zone: EMOTORAD_TEST_RIDE_SCHEDULE_ZONE});
            if (parsed.isValid === true) {
                return parsed;
            }
        }
        const asJs = new Date(trimmed);
        if (Number.isNaN(asJs.getTime()) === true) {
            return null;
        }
        return DateTime.fromJSDate(asJs).setZone(EMOTORAD_TEST_RIDE_SCHEDULE_ZONE);
    }
    return null;
}

/**
 * Test ride updates send one combined UTC instant on the test ride date column.
 * When `Test ride Time` (`HH:mm`) is present, the clock comes from that column only; the calendar
 * day is taken from `Test Ride Date` (any time-of-day shown in that field is ignored for merging).
 * When the time column is empty, the date column value is used as the full instant as-is.
 * The time column is always omitted from the outgoing payload.
 */
export function applyEmotoradTestRideDateTimeSplitToEventApiPayload(payload: Record<Uuid, unknown>): void {
    const dateId = EMOTORAD__EVENT__TESTRIDES__TEST_RIDE_DATE__COLUMN_ID;
    const timeId = EMOTORAD__EVENT__TESTRIDES__TEST_RIDE_TIME__COLUMN_ID;

    const timeRaw = payload[timeId];
    const timeText = timeRaw !== null && timeRaw !== undefined ? String(timeRaw).trim() : "";
    const normalizedSlot = normalizeEmotoradTestRideSlotHhMmString(timeText);

    let merged: DateTime | null =
        Object.prototype.hasOwnProperty.call(payload, String(dateId)) === true
            ? parseTestRideDateColumnInstantForSplit(payload[dateId])
            : null;

    if (merged !== null && merged.isValid === true && normalizedSlot !== "") {
        const dayStart = merged.setZone(EMOTORAD_TEST_RIDE_SCHEDULE_ZONE).startOf("day");
        const timeMatch = /^(\d{1,2}):(\d{2})$/.exec(normalizedSlot);
        if (timeMatch !== null) {
            const hour = Number(timeMatch[1]);
            const minute = Number(timeMatch[2]);
            if (!Number.isNaN(hour) && !Number.isNaN(minute)) {
                merged = dayStart.set({hour, minute, second: 0, millisecond: 0});
            }
        }
    }

    if (merged !== null && merged.isValid === true) {
        const iso = merged.setZone(EMOTORAD_TEST_RIDE_SCHEDULE_ZONE).toUTC().toISO();
        if (iso !== null) {
            payload[dateId] = iso;
        }
    }

    delete payload[timeId];
}

function hour24To12hOnTheHourLabel(hour24: number): string {
    const h = hour24;
    const suffix = h >= 12 ? "PM" : "AM";
    const hour12 = h === 0 ? 12 : h === 12 ? 12 : h > 12 ? h - 12 : h;
    return `${hour12}:00 ${suffix}`;
}

/** 24h clock + minutes → `6:00 PM` style (12h, space before AM/PM). */
function hhMm24To12hLabel(hour24: number, minute: number): string {
    const suffix = hour24 >= 12 ? "PM" : "AM";
    const hour12 = hour24 === 0 ? 12 : hour24 > 12 ? hour24 - 12 : hour24 === 12 ? 12 : hour24;
    return `${hour12}:${String(minute).padStart(2, "0")} ${suffix}`;
}

/** One-hour slots for the full day: 12:00 AM–1:00 AM through 11:00 PM–12:00 AM. Values stay `HH:00` (24h) for APIs. */
export function getEmotoradTestRideHourlyTimeSlotOptions(): EmotoradTestRideTimeOption[] {
    const out: EmotoradTestRideTimeOption[] = [];
    for (let startHour = 0; startHour <= 23; startHour += 1) {
        const endHour = (startHour + 1) % 24;
        const label = `${hour24To12hOnTheHourLabel(startHour)}- ${hour24To12hOnTheHourLabel(endHour)}`;
        const value = `${String(startHour).padStart(2, "0")}:00`;
        out.push({value, label});
    }
    return out;
}

/** Same slot values as {@link getEmotoradTestRideHourlyTimeSlotOptions}; labels use {@link formatEmotoradTestRideShortRangeLabelFromSlotHhMm} (12h AM/PM windows). */
export function getEmotoradTestRideHourlyTimeSlotOptionsShortRange(): EmotoradTestRideTimeOption[] {
    const out: EmotoradTestRideTimeOption[] = [];
    for (let startHour = 0; startHour <= 23; startHour += 1) {
        const value = `${String(startHour).padStart(2, "0")}:00`;
        out.push({
            value,
            label: formatEmotoradTestRideShortRangeLabelFromSlotHhMm(value),
        });
    }
    return out;
}

export function splitEmotoradTestRideStoredInstantToPickerDateAndSlot(raw: unknown): {
    pickerDate: Date | null;
    slotHhMm: string;
} {
    const dt = parseTestRideDateColumnInstantForSplit(raw);
    if (dt === null || dt.isValid === false) {
        return {pickerDate: null, slotHhMm: ""};
    }
    const onCalendar = dt.startOf("day");
    const pickerDate = new Date(onCalendar.year, onCalendar.month - 1, onCalendar.day);
    const floored = dt.startOf("hour");
    const slotHhMm = `${String(floored.hour).padStart(2, "0")}:00`;
    return {pickerDate, slotHhMm};
}

/**
 * Mantine `DatePickerInput` may pass `onChange` a `Date` or a string matching `valueFormat`
 * (e.g. `dd/MM/yyyy`). Merge helpers need a real `Date` at that calendar day in the local JS sense.
 */
export function parseEmotoradTestRideDatePickerDayToLocalDate(input: unknown): Date | null {
    if (input === null || input === undefined) {
        return null;
    }
    if (input instanceof Date) {
        return Number.isNaN(input.getTime()) === true ? null : input;
    }
    if (typeof input === "string") {
        const trimmed = input.trim();
        if (trimmed === "") {
            return null;
        }
        const fmts = [
            "dd/MM/yyyy",
            "d/M/yyyy",
            "dd/M/yyyy",
            "d/MM/yyyy",
            "yyyy-MM-dd",
            "yyyy/MM/dd",
        ] as const;
        for (const fmt of fmts) {
            const dt = DateTime.fromFormat(trimmed, fmt, {zone: EMOTORAD_TEST_RIDE_SCHEDULE_ZONE});
            if (dt.isValid === true) {
                return new Date(dt.year, dt.month - 1, dt.day);
            }
        }
        const iso = DateTime.fromISO(trimmed, {setZone: true});
        if (iso.isValid === true) {
            const zoned = iso.setZone(EMOTORAD_TEST_RIDE_SCHEDULE_ZONE);
            return new Date(zoned.year, zoned.month - 1, zoned.day);
        }
        const asJs = new Date(trimmed);
        return Number.isNaN(asJs.getTime()) === true ? null : asJs;
    }
    return null;
}

/**
 * Normalizes test-ride time strings to padded `HH:mm` for merging with a calendar day.
 * Accepts legacy `HH:mm` plus range-style labels such as `10:00-11:00` (start window is used).
 */
export function normalizeEmotoradTestRideSlotHhMmString(raw: string): string {
    const trimmed = raw.trim();
    if (trimmed === "") {
        return "";
    }
    const exact = /^(\d{1,2}):(\d{1,2})$/.exec(trimmed);
    if (exact !== null) {
        const hour = Number(exact[1]);
        const minute = Number(exact[2]);
        if (Number.isNaN(hour) === false && Number.isNaN(minute) === false) {
            return `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
        }
        return "";
    }
    const rangeStart = /^(\d{1,2}):(\d{1,2})\s*-\s*/.exec(trimmed);
    if (rangeStart !== null) {
        const hour = Number(rangeStart[1]);
        const minute = Number(rangeStart[2]);
        if (Number.isNaN(hour) === false && Number.isNaN(minute) === false) {
            return `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
        }
    }
    return "";
}

/** Calendar day from the date picker (local Y/M/D) plus `HH:mm` slot, interpreted in Asia/Kolkata. */
export function combineEmotoradTestRidePickerDateAndSlotHhMmToUtcIso(
    pickerDate: Date | null,
    slotHhMm: string,
): string | null {
    if (pickerDate === null) {
        return null;
    }
    const trimmed = normalizeEmotoradTestRideSlotHhMmString(slotHhMm);
    if (trimmed === "") {
        return null;
    }
    const timeMatch = /^(\d{1,2}):(\d{2})$/.exec(trimmed);
    if (timeMatch === null) {
        return null;
    }
    const hour = Number(timeMatch[1]);
    const minute = Number(timeMatch[2]);
    if (Number.isNaN(hour) === true || Number.isNaN(minute) === true) {
        return null;
    }
    const y = pickerDate.getFullYear();
    const mo = pickerDate.getMonth() + 1;
    const d = pickerDate.getDate();
    const merged = DateTime.fromObject(
        {year: y, month: mo, day: d, hour, minute, second: 0, millisecond: 0},
        {zone: EMOTORAD_TEST_RIDE_SCHEDULE_ZONE},
    );
    if (merged.isValid === false) {
        return null;
    }
    return merged.toUTC().toISO();
}

/** Kolkata calendar date only from the stored test-ride instant (or parseable string). */
export function formatEmotoradTestRideDateOnlyForDisplay(raw: unknown): string {
    const dt = parseTestRideDateColumnInstantForSplit(raw);
    if (dt === null || dt.isValid === false) {
        return "-";
    }
    return dt.toFormat("d LLL yyyy");
}

type EmotoradTestRideEventRowLike = {
    additionalColumnValues: Record<Uuid, unknown>;
    eventContainer: {additionalColumnValues: Record<Uuid, unknown>};
};

/**
 * Slot `HH:mm` for UI/sorting: use the test ride **date** column instant (Asia/Kolkata) as the source
 * of truth when it disagrees with the separate time column — the API persists the clock on the date
 * instant, and list payloads can briefly keep a stale time string.
 * Otherwise prefer a normalized time column when present, else derive from the date instant only.
 */
export function getEmotoradTestRideEffectiveSlotHhMmFromEventRow(row: EmotoradTestRideEventRowLike): string {
    const timeId = EMOTORAD__EVENT__TESTRIDES__TEST_RIDE_TIME__COLUMN_ID;
    const dateId = EMOTORAD__EVENT__TESTRIDES__TEST_RIDE_DATE__COLUMN_ID;
    const timeRaw = row.additionalColumnValues[timeId] ?? row.eventContainer.additionalColumnValues[timeId];
    const timeStr = typeof timeRaw === "string" ? timeRaw.trim() : String(timeRaw ?? "").trim();
    const fromTime = timeStr !== "" ? normalizeEmotoradTestRideSlotHhMmString(timeStr) : "";

    const dateRaw = row.additionalColumnValues[dateId] ?? row.eventContainer.additionalColumnValues[dateId];
    const fromDate = splitEmotoradTestRideStoredInstantToPickerDateAndSlot(dateRaw).slotHhMm;

    if (fromDate !== "" && fromTime !== "" && fromTime !== fromDate) {
        return fromDate;
    }
    if (fromTime !== "") {
        return fromTime;
    }
    return fromDate;
}

/** `10:00` → `10:00 AM - 11:00 AM` (12h clock, one-hour window, Asia/Kolkata) for table/panels; unknown strings returned as-is. */
export function formatEmotoradTestRideShortRangeLabelFromSlotHhMm(slotHhMm: string): string {
    const trimmed = slotHhMm.trim();
    const m = /^(\d{1,2}):(\d{2})$/.exec(trimmed);
    if (m === null) {
        return trimmed;
    }
    const hour = Number(m[1]);
    const minute = Number(m[2]);
    if (Number.isNaN(hour) === true || Number.isNaN(minute) === true) {
        return trimmed;
    }
    const start = DateTime.fromObject(
        {hour, minute, second: 0, millisecond: 0},
        {zone: EMOTORAD_TEST_RIDE_SCHEDULE_ZONE},
    );
    if (start.isValid === false) {
        return trimmed;
    }
    const end = start.plus({hours: 1});
    const startLabel = hhMm24To12hLabel(start.hour, start.minute);
    const endLabel = hhMm24To12hLabel(end.hour, end.minute);
    return `${startLabel} - ${endLabel}`;
}

/** Map stored `HH:00` (or `H:00`) slot values to the same labels as schedule/log forms. */
export function formatEmotoradTestRideTimeSlotDisplay(value: string): string {
    const trimmed = value.trim();
    if (trimmed === "") {
        return "";
    }
    const options = getEmotoradTestRideHourlyTimeSlotOptions();
    const direct = options.find((o) => o.value === trimmed);
    if (direct !== undefined) {
        return direct.label;
    }
    const hourMatch = /^(\d{1,2}):(\d{2})$/.exec(trimmed);
    if (hourMatch !== null) {
        const hour = Number(hourMatch[1]);
        const minute = Number(hourMatch[2]);
        if (!Number.isNaN(hour) && !Number.isNaN(minute) && minute === 0) {
            const padded = `${String(hour).padStart(2, "0")}:00`;
            const fromPadded = options.find((o) => o.value === padded);
            if (fromPadded !== undefined) {
                return fromPadded.label;
            }
        }
    }
    return trimmed;
}
